import React, { useEffect, useState } from 'react'
import { Button, Modal } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import './DocumentationDialog.scss'

const allDocs = require('../../docs/all-docs.json')

interface DocumentationDialogProps {
  show: boolean
  onHide: () => void
  docKey: string
  sectionTitles?: string[]
}

const MIN_FONT_SIZE = 12
const MAX_FONT_SIZE = 32
const DEFAULT_FONT_SIZE = 16
const FONT_SIZE_STEP = 2

export const DocumentationDialog: React.FC<DocumentationDialogProps> = ({ show, onHide, docKey, sectionTitles = [] }) => {
  const { t, i18n } = useTranslation()
  const currentLang = i18n.language || 'en'
  const lang = currentLang.startsWith('sr') ? 'sr' : 'en'
  const [isFullscreen, setIsFullscreen] = useState(false)
  const [fontSize, setFontSize] = useState(DEFAULT_FONT_SIZE)

  const docsDictionary = (allDocs as Record<string, Record<string, any>>)[lang] || {}
  const docs = docsDictionary?.[docKey as keyof typeof docsDictionary] || null

  useEffect(() => {
    if (!show) {
      setIsFullscreen(false)
      setFontSize(DEFAULT_FONT_SIZE)
    }
  }, [show])

  if (!docs) {
    return null
  }

  const normalizedSectionTitles = sectionTitles.map((title) => title.trim().toLowerCase()).filter(Boolean)

  const filterSections = (sections: any[]) => {
    if (!sections || sections.length === 0) {
      return []
    }
    if (!normalizedSectionTitles.length) {
      return sections
    }
    const matches = sections.filter((section) => section?.title && normalizedSectionTitles.includes(section.title.trim().toLowerCase()))
    return matches.length ? matches : sections
  }

  const renderContent = () => {
    const sections = filterSections(docs.sections || [])
    if (sections.length) {
      return sections.map((section: any, index: number) => (
        <section key={index} className="mb-4">
          {section.title && <h5>{section.title}</h5>}
          {section.description && <p>{section.description}</p>}

          {section.table && section.list && (
            <>
              <div className="table-responsive">
                <table className="table table-sm table-bordered">
                  <thead>
                    <tr>
                      {Object.values(section.table).map((header: any, idx: number) => (
                        <th key={idx}>{header}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {section.list.map((item: any, idx: number) => (
                      <tr key={idx}>
                        {Object.values(item).map((value: any, cellIdx: number) => (
                          <td key={cellIdx}>{typeof value === 'string' && value.startsWith('{{') ? <code>{value}</code> : value}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}

          {section.examples && (
            <div className="mb-3">
              {section.examples.map((example: any, exIdx: number) => (
                <div key={exIdx} className="mb-3">
                  {example.title && <h6>{example.title}</h6>}
                  {example.description && <p>{example.description}</p>}
                  {example.code && (
                    <pre className="bg-light p-3 rounded">
                      <code>{example.code}</code>
                    </pre>
                  )}
                  {example.result && <p className="text-muted small">{example.result}</p>}
                </div>
              ))}
            </div>
          )}

          {section.items && (
            <ul>
              {section.items.map((item: string, idx: number) => {
                const parts = item.split(/(\*\*.*?\*\*)/g)
                return (
                  <li key={idx}>
                    {parts.map((part, partIdx) => {
                      if (part.startsWith('**') && part.endsWith('**')) {
                        return <strong key={partIdx}>{part.slice(2, -2)}</strong>
                      }
                      return <span key={partIdx}>{part}</span>
                    })}
                  </li>
                )
              })}
            </ul>
          )}

          {section.subsections && (
            <div className="mt-3">
              {section.subsections.map((subsection: any, subIdx: number) => (
                <div key={subIdx} className="mb-3">
                  {subsection.title && <h6>{subsection.title}</h6>}
                  {subsection.description && <p>{subsection.description}</p>}

                  {subsection.table && subsection.list && (
                    <>
                      <div className="table-responsive">
                        <table className="table table-sm table-bordered">
                          <thead>
                            <tr>
                              {Object.values(subsection.table).map((header: any, idx: number) => (
                                <th key={idx}>{header}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {subsection.list.map((item: any, idx: number) => (
                              <tr key={idx}>
                                {Object.values(item).map((value: any, cellIdx: number) => (
                                  <td key={cellIdx}>{typeof value === 'string' && value.startsWith('{{') ? <code>{value}</code> : value}</td>
                                ))}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </>
                  )}

                  {subsection.items && (
                    <ul>
                      {subsection.items.map((item: string, idx: number) => {
                        const parts = item.split(/(\*\*.*?\*\*)/g)
                        return (
                          <li key={idx}>
                            {parts.map((part, partIdx) => {
                              if (part.startsWith('**') && part.endsWith('**')) {
                                return <strong key={partIdx}>{part.slice(2, -2)}</strong>
                              }
                              return <span key={partIdx}>{part}</span>
                            })}
                          </li>
                        )
                      })}
                    </ul>
                  )}
                </div>
              ))}
            </div>
          )}
        </section>
      ))
    }

    return null
  }

  const showOverview = !normalizedSectionTitles.length && docs.overview

  const toggleFullscreen = () => {
    setIsFullscreen(!isFullscreen)
  }

  const increaseFontSize = () => {
    setFontSize((prev) => Math.min(prev + FONT_SIZE_STEP, MAX_FONT_SIZE))
  }

  const decreaseFontSize = () => {
    setFontSize((prev) => Math.max(prev - FONT_SIZE_STEP, MIN_FONT_SIZE))
  }

  return (
    <Modal
      show={show}
      onHide={onHide}
      size={isFullscreen ? undefined : 'lg'}
      backdrop="static"
      scrollable
      centered={!isFullscreen}
      fullscreen={isFullscreen ? true : undefined}
      className={`documentation-dialog ${isFullscreen ? 'fullscreen' : ''}`}
      backdropClassName="documentation-dialog-backdrop"
    >
      <Modal.Header>
        <Modal.Title>{docs.title}</Modal.Title>
        <div className="d-flex align-items-center gap-2 ms-auto">
          <Button
            variant="link"
            size="sm"
            onClick={decreaseFontSize}
            disabled={fontSize <= MIN_FONT_SIZE}
            className="p-1"
            title={t('decrease_font_size') || 'Smanji font'}
            style={{ minWidth: '32px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          >
            <i className="bi bi-dash-lg"></i>
          </Button>
          <span className="small text-muted" style={{ minWidth: '40px', textAlign: 'center' }}>
            {fontSize}px
          </span>
          <Button
            variant="link"
            size="sm"
            onClick={increaseFontSize}
            disabled={fontSize >= MAX_FONT_SIZE}
            className="p-1"
            title={t('increase_font_size') || 'Povećaj font'}
            style={{ minWidth: '32px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          >
            <i className="bi bi-plus-lg"></i>
          </Button>
          <Button
            variant="link"
            size="sm"
            onClick={toggleFullscreen}
            className="p-1"
            title={isFullscreen ? t('exit_fullscreen') || 'Izađi iz punog ekrana' : t('fullscreen') || 'Puni ekran'}
            style={{ minWidth: '32px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          >
            <i className={`bi ${isFullscreen ? 'bi-fullscreen-exit' : 'bi-fullscreen'}`}></i>
          </Button>
          <Button
            variant="link"
            size="sm"
            onClick={onHide}
            className="p-1"
            title={t('close') || 'Zatvori'}
            style={{ minWidth: '32px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          >
            <i className="bi bi-x-lg"></i>
          </Button>
        </div>
      </Modal.Header>
      <Modal.Body style={{ maxHeight: isFullscreen ? 'calc(100vh - 80px)' : '70vh', overflowY: 'auto' }}>
        <div className="documentation-content" style={{ fontSize: `${fontSize}px` }}>
          {showOverview && (
            <section className="mb-4">
              {docs.overview.title && <h5>{docs.overview.title}</h5>}
              {docs.overview.description && <p>{docs.overview.description}</p>}
            </section>
          )}
          {renderContent()}
        </div>
      </Modal.Body>
    </Modal>
  )
}
